import type {
    TranscriptTextType,
    ViewType,
    Variant,
    Work,
} from "@/types/document";

import {
    getHtmlForVariant,
    translateAlignmentPoint,
} from "@/helpers/manuscripts";

import {
    applyEditionPlusPreset,
    applyTranscriptionPlusPreset,
} from "@/helpers/textDisplayPresets";

import { TextFilter } from "@/helpers/textSettings";

// Type definitions
type ContainerType = "mainframe" | "ancillary";

type ActiveViewType = "blank" | "mainframe" | ViewType;

type NullableString = string | null;

type DashboardTextFilter = TextFilter & {
    settings: {
        displayProseVersion?: boolean;
        [key: string]: unknown;
    };
};

type DashboardActionContext = {
    state: DashboardState;
    commit: (mutation: string, payload?: unknown) => void;
    dispatch: (action: string, payload?: unknown) => Promise<unknown>;
};

interface AvailableView {
    id: number;
    isSelectable: boolean;
    name: string;
    type: Exclude<ActiveViewType, "blank">;
}

interface TocNavigation {
    selectedTocId: NullableString;
    prevTocId: NullableString;
    nextTocId: NullableString;
}

interface Container {
    id: string;
    type: ContainerType;
    height: number;
    activeView: ActiveViewType;
    htmlContent: string;
    renderedPages: unknown | null;
    selectedWork?: Work;
    selectedVariant?: Variant | null;
    textFilter: DashboardTextFilter;
    tocNavigation: TocNavigation;
    pendingTextPart: string | number | null;
    pendingVerse: string | number | null;
}

interface MsAndVerseToOpen {
    textPart: string | number | null;
    siglum: string | null;
    verse: string | number | null;
    hasEdition: boolean;
    openComment: boolean;
}

interface DashboardState {
    availableViews: AvailableView[];
    dashboardColumns: number;
    dashboardLines: number;
    mainContainerContent: Container;
    printContent: unknown | null;
    scrollGranularity: number;
    sideContainers: Container[];
    setMsAndVerseToOpen: MsAndVerseToOpen | null;
    lineContents: string[][];
}

interface SetNewViewTypePayload {
    id: string;
    newView: ActiveViewType;
}

interface ChangeActiveViewPayload {
    id: string;
    viewtype: ActiveViewType;
}

interface ChangeDashboardItemTypePayload {
    id: string;
    itemType: ContainerType;
}

interface SetRenderedPagesPayload {
    containerId: string;
    pageFlow: unknown;
}

interface SetSelectedVariantPayload {
    id: string;
    variant: Variant;
    shouldUseProseVersion?: boolean;
    pendingTextPart?: string | number | null;
    pendingVerse?: string | number | null;
}

interface SetTocNavigationPayload {
    containerId: string;
    tocInfo: {
        selected: string;
        prev: NullableString;
        next: NullableString;
    };
}

interface PendingTextPartPayload {
    id: string;
    pendingTextPart: string | number | null;
}

interface PendingVersePayload {
    id: string;
    pendingVerse: string | number | null;
}

interface SelectedTocIdPayload {
    id: string;
    selectedTocId: NullableString;
}

interface SelectVariantForContainerPayload {
    id: string;
    variant: Variant;
    desiredTranscriptType?: TranscriptTextType;
    pendingTextPart?: string | number | null;
    pendingVerse?: string | number | null;
}

// Constants
const DASHBOARD_ITEM_SIZE = 800;
const EMPTY_ITEM_SIZE = 150;
const initialMainContainerId = uuidv4();
const initialSideContainerId = uuidv4();

const createEmptyContainer = (id = uuidv4()): Container => ({
    id,
    height: EMPTY_ITEM_SIZE,
    type: "ancillary",
    activeView: "blank",
    htmlContent: "",
    renderedPages: null,
    selectedWork: undefined,
    selectedVariant: undefined,
    textFilter: new TextFilter() as DashboardTextFilter,
    tocNavigation: {
        selectedTocId: null,
        prevTocId: null,
        nextTocId: null,
    },
    pendingTextPart: null,
    pendingVerse: null,
});

const createMainContainer = (id = uuidv4()): Container => ({
    id,
    type: "mainframe",
    height: DASHBOARD_ITEM_SIZE,
    activeView: "transcript",
    htmlContent: "",
    renderedPages: null,
    selectedWork: undefined,
    selectedVariant: undefined,
    textFilter: new TextFilter() as DashboardTextFilter,
    tocNavigation: {
        selectedTocId: null,
        prevTocId: null,
        nextTocId: null,
    },
    pendingTextPart: null,
    pendingVerse: null,
});

// Initial state
const state: DashboardState = {
    availableViews: [
        {
            id: 1,
            isSelectable: true,
            name: "Faksimile",
            type: "facsimile",
        },
        {
            id: 2,
            isSelectable: true,
            name: "Transkription",
            type: "transcript",
        },
        {
            id: 3,
            isSelectable: true,
            name: "Liste der Varianten",
            type: "variant-list",
        },
        {
            id: 4,
            isSelectable: false,
            name: "Leithandschrift",
            type: "mainframe",
        },
        {
            id: 5,
            isSelectable: false,
            name: "Übersetzung",
            type: "translation",
        },
        {
            id: 6,
            isSelectable: true,
            name: "Kommentar",
            type: "comment",
        },
    ],
    dashboardColumns: 2, // TODO: remove
    dashboardLines: 1,
    mainContainerContent: createMainContainer(initialMainContainerId),
    printContent: null,
    scrollGranularity: 1,
    sideContainers: [createEmptyContainer(initialSideContainerId)],
    setMsAndVerseToOpen: null,
    // array holding an array of one array per line containing the line's container ids
    lineContents: [[initialMainContainerId, initialSideContainerId]],
};

const getters = {
    getActiveView:
        (state: DashboardState) =>
        (isMain: boolean, id: string): ActiveViewType | undefined => {
            if (isMain) {
                return state.mainContainerContent.activeView;
            }

            return state.sideContainers.find((c) => c.id === id)?.activeView;
        },

    getContainerById:
        (state: DashboardState) =>
        (id: string): Container => {
            return getContainerById(state, id);
        },
};

const actions = {
    async selectVariantForContainer(
        { state, commit }: DashboardActionContext,
        {
            id,
            variant,
            desiredTranscriptType = "edition",
            pendingTextPart = null,
            pendingVerse = null,
        }: SelectVariantForContainerPayload,
    ): Promise<void> {
        const container = getContainerById(state, id);
        const work = state.mainContainerContent.selectedWork;

        if (!work) {
            throw new Error(
                "Cannot select variant before selected work is set.",
            );
        }

        const textSettings = container.textFilter.settings;
        const supportsEdition = variant.isLeader === true;

        const transcriptTextType: TranscriptTextType = supportsEdition
            ? desiredTranscriptType
            : "transcription";

        const displayEdition = transcriptTextType === "edition";

        const shouldUseProseVersion =
            displayEdition && !!(variant.hasProseEmbedded || variant.isProse);

        if (displayEdition) {
            applyEditionPlusPreset(textSettings, shouldUseProseVersion);
        } else {
            applyTranscriptionPlusPreset(textSettings);
        }

        commit("setSelectedVariantState", {
            id,
            variant,
            shouldUseProseVersion,
            pendingTextPart,
            pendingVerse,
        });

        const result = await getHtmlForVariant(
            work.workId,
            variant.id,
            shouldUseProseVersion,
            variant.isLeader,
        );

        commit("setContainerHtmlContent", {
            id,
            htmlContent: normalizeHtmlContent(result),
        });

        commit("changeActiveView", {
            id,
            viewtype: "transcript",
        });
    },

    async setNewViewType(
        { commit, dispatch }: DashboardActionContext,
        { id, newView }: SetNewViewTypePayload,
    ): Promise<void> {
        return dispatch("validateViewType", newView).then((viewtype) => {
            commit("changeActiveView", { id, viewtype });
        });
    },

    validateViewType(
        { state }: DashboardActionContext,
        viewtype: ActiveViewType,
    ): Promise<ActiveViewType> {
        return new Promise((resolve, reject) => {
            if (state.availableViews.find((v) => v.type === viewtype)) {
                resolve(viewtype);
            } else {
                reject(new Error(`Invalid view type: ${viewtype}`));
            }
        });
    },
};

const mutations = {
    addDashboardItem(state: DashboardState): void {
        state.sideContainers.push(createEmptyContainer());
    },

    addDashboardItemToLine(state: DashboardState, lineIndex: number): void {
        const newId = uuidv4();

        if (lineIndex >= state.dashboardLines) {
            state.dashboardLines = state.dashboardLines + 1;
            state.lineContents.push([newId]);
        } else {
            state.lineContents[lineIndex].push(newId);
        }

        state.sideContainers.push(createEmptyContainer(newId));
    },

    // add a dashboard item to a new line instead
    addDashboardLine(state: DashboardState): void {
        const newId = uuidv4();
        state.dashboardLines = state.dashboardLines + 1;
        state.lineContents.push([newId]);
        state.sideContainers.push(createEmptyContainer(newId));
    },

    changeActiveView(
        state: DashboardState,
        { id, viewtype }: ChangeActiveViewPayload,
    ): void {
        if (state.mainContainerContent.id === id) {
            state.mainContainerContent.activeView = viewtype;
        } else {
            const container = getContainerById(state, id);
            container.activeView = viewtype;
            container.height = DASHBOARD_ITEM_SIZE;
        }
    },

    changeDashboardItemType(
        state: DashboardState,
        { id, itemType }: ChangeDashboardItemTypePayload,
    ): void {
        const container = getContainerById(state, id);
        container.type = itemType;
    },

    closeDashBoardItem(state: DashboardState, id: string): void {
        const storedItem = state.sideContainers.find((item) => item.id === id);

        if (storedItem) {
            const itemIndex = state.sideContainers.indexOf(storedItem);

            if (itemIndex !== -1) {
                state.sideContainers.splice(itemIndex, 1);
            }
        }

        for (let i = state.lineContents.length - 1; i >= 0; i--) {
            const ln = state.lineContents[i];

            while (ln.includes(id)) {
                const index = ln.indexOf(id);
                ln.splice(index, 1);

                if (ln.length === 0) {
                    state.lineContents.splice(i, 1);
                    break;
                }
            }
        }
    },

    resetMainContainer(state: DashboardState): void {
        const newId = uuidv4();

        state.mainContainerContent = createMainContainer(newId);
        state.lineContents[0][0] = newId;

        // when main container changes, we need to reset selected variants in all other open containers...
        state.sideContainers.forEach((c) => {
            c.selectedVariant = undefined;
            c.htmlContent = "";
            c.tocNavigation = {
                selectedTocId: null,
                prevTocId: null,
                nextTocId: null,
            };
            c.pendingTextPart = null;
            c.pendingVerse = null;
        });
    },

    resetMainContainerVariant(state: DashboardState): void {
        const { mainContainerContent } = state;
        mainContainerContent.htmlContent = "";
        mainContainerContent.selectedVariant = undefined;
        mainContainerContent.tocNavigation = {
            selectedTocId: null,
            prevTocId: null,
            nextTocId: null,
        };
        mainContainerContent.pendingTextPart = null;
        mainContainerContent.pendingVerse = null;
    },

    setDashboardColumns(state: DashboardState, numOfColumns: number): void {
        if (0 < numOfColumns && numOfColumns < 4) {
            state.dashboardColumns = numOfColumns;
        } else {
            throw new Error(
                "Cannot set number of dashboard columns to " + numOfColumns,
            );
        }
    },

    setPrintContent(state: DashboardState, ref: unknown): void {
        state.printContent = ref;
    },

    setRenderedPages(
        state: DashboardState,
        { containerId, pageFlow }: SetRenderedPagesPayload,
    ): void {
        const container = getContainerById(state, containerId);
        container.renderedPages = pageFlow;
    },

    setSelectedVariantState(
        state: DashboardState,
        {
            id,
            variant,
            shouldUseProseVersion,
            pendingTextPart = null,
            pendingVerse = null,
        }: SetSelectedVariantPayload,
    ): void {
        const container = getContainerById(state, id);

        container.textFilter.settings.displayProseVersion =
            shouldUseProseVersion === true;

        container.selectedVariant = variant;

        container.tocNavigation = {
            selectedTocId: null,
            prevTocId: null,
            nextTocId: null,
        };

        container.pendingTextPart = pendingTextPart;
        container.pendingVerse = pendingVerse;
    },

    setContainerHtmlContent(
        state: DashboardState,
        { id, htmlContent }: { id: string; htmlContent: string },
    ): void {
        const container = getContainerById(state, id);
        container.htmlContent = htmlContent;
    },

    setTocNavigation(
        state: DashboardState,
        { containerId, tocInfo }: SetTocNavigationPayload,
    ): void {
        const container = getContainerById(state, containerId);
        const selectedVariant = container.selectedVariant;

        if (!selectedVariant) {
            return;
        }

        const variantId = selectedVariant.id;
        const isLeader = selectedVariant.isLeader;
        const hasLeaders = selectedVariant.hasLeaders || [];
        const itsLeader =
            !isLeader && hasLeaders.length > 0 ? hasLeaders[0] : null;

        // 1) update TOC info of containers with same text
        const containersWithSameVariant = getAllContainersWithSameVariant(
            state,
            variantId,
        );
        containersWithSameVariant.forEach((containerToUpdate) => {
            containerToUpdate.tocNavigation = {
                selectedTocId: tocInfo.selected,
                prevTocId: tocInfo.prev,
                nextTocId: tocInfo.next,
            };
        });

        // 2) update TOC info of containers with text belonging to same redaction group
        const containersWithParallelVariant =
            getAllContainersWithParallelVariant(state, variantId);

        // retrieve equivalent textgroup n values / long text line numbers
        const toc =
            selectedVariant.textTOC?.filter(
                (item) => item.type !== "comment",
            ) || [];
        const textType = selectedVariant.isLongTextOrTextGroup;
        const collationTables = selectedVariant.collationTables || [];
        const selectedTocIdN = toc.find((i) => i.id === tocInfo.selected)?.n; // for text groups
        const selectedTocLineN = toc.find(
            (i) => i.id === tocInfo.selected,
        )?.lineN; // for long texts
        let selectedTocLineNColl: string | number | null = null;

        if (
            textType === "longText" &&
            selectedTocLineN !== undefined &&
            collationTables.length > 0
        ) {
            const collationTable = collationTables.find(
                (t) => t.leader === variantId || t.leader === itsLeader,
            );

            if (collationTable) {
                if (collationTable.leader === variantId) {
                    selectedTocLineNColl =
                        (translateAlignmentPoint(
                            selectedTocLineN,
                            null,
                            collationTable.leaderLines,
                            collationTable.variantLines,
                        ) as string | number | null) || null;
                } else {
                    selectedTocLineNColl =
                        (translateAlignmentPoint(
                            selectedTocLineN,
                            null,
                            collationTable.variantLines,
                            collationTable.leaderLines,
                        ) as string | number | null) || null;
                }
            }
        }

        containersWithParallelVariant.forEach((containerToUpdate) => {
            // check if container has equivalent id
            const containersToc = containerToUpdate.selectedVariant?.textTOC;
            let containersSelectedTocId: string | null = null;

            if (textType === "textGroup" && selectedTocIdN) {
                containersSelectedTocId =
                    containersToc?.find((i) => i.n === selectedTocIdN)?.id ||
                    null;
            } else if (textType === "longText" && selectedTocLineNColl) {
                containersSelectedTocId =
                    [...(containersToc || [])]
                        .reverse()
                        .find(
                            (i) =>
                                Number(i.lineN) <= Number(selectedTocLineNColl),
                        )?.id || null; // TODO: test 46 & 157
            }

            // update only if equivalent id found
            if (containersSelectedTocId && containersToc) {
                const currentIndex = containersToc.findIndex(
                    (i) => i.id === containersSelectedTocId,
                );

                containerToUpdate.tocNavigation = {
                    selectedTocId: containersSelectedTocId,
                    prevTocId: containersToc[currentIndex - 1]?.id || null,
                    nextTocId: containersToc[currentIndex + 1]?.id || null,
                };
            }
        });
    },

    setMsAndVerseToOpen(
        state: DashboardState,
        { textPart, siglum, verse, hasEdition, openComment }: MsAndVerseToOpen,
    ): void {
        state.setMsAndVerseToOpen = {
            textPart,
            siglum,
            verse,
            hasEdition,
            openComment,
        };
    },

    clearMsAndVerseToOpen(state: DashboardState): void {
        state.setMsAndVerseToOpen = null;
    },

    setMainSelectedWork(state: DashboardState, work: Work): void {
        state.mainContainerContent.selectedWork = work;
    },

    clearSideContainerSelectedVariants(state: DashboardState): void {
        state.sideContainers.forEach((container) => {
            container.selectedVariant = null;
        });
    },

    setContainerPendingTextPart(
        state: DashboardState,
        { id, pendingTextPart }: PendingTextPartPayload,
    ): void {
        const container = getContainerById(state, id);
        container.pendingTextPart = pendingTextPart;
    },

    setContainerPendingVerse(
        state: DashboardState,
        { id, pendingVerse }: PendingVersePayload,
    ): void {
        const container = getContainerById(state, id);
        container.pendingVerse = pendingVerse;
    },

    setContainerSelectedTocId(
        state: DashboardState,
        { id, selectedTocId }: SelectedTocIdPayload,
    ): void {
        const container = getContainerById(state, id);
        container.tocNavigation.selectedTocId = selectedTocId;
    },
};

// Helper functions
function uuidv4(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function getContainerById(state: DashboardState, id: string): Container {
    if (state.mainContainerContent.id === id) {
        return state.mainContainerContent;
    }

    const container = state.sideContainers.find((c) => c.id === id);

    if (!container) {
        throw new Error(`Container with id ${id} was not found.`);
    }

    return container;
}

function getAllContainersWithSameVariant(
    state: DashboardState,
    variantId: string,
): Container[] {
    const containers: Container[] = [];
    const allContainers = [state.mainContainerContent, ...state.sideContainers];

    allContainers.forEach((container) => {
        if (container.selectedVariant?.id === variantId) {
            containers.push(container);
        }
    });

    return containers;
}

function getAllContainersWithParallelVariant(
    state: DashboardState,
    variantId: string,
): Container[] {
    const containers: Container[] = [];
    const allContainers = [state.mainContainerContent, ...state.sideContainers];

    allContainers.forEach((container) => {
        const containerIsLeader = container.selectedVariant?.isLeader;
        const containerHasLeaders = container.selectedVariant?.hasLeaders || [];
        const containersLeader =
            !containerIsLeader && containerHasLeaders.length > 0
                ? containerHasLeaders[0]
                : null;

        // apply commented condition to include only containers with parallel variant
        // if ((itsLeader && container.selectedVariant?.id === itsLeader) || (isLeader && containersLeader === variantId)) {
        void containersLeader;

        if (container.selectedVariant?.id !== variantId) {
            containers.push(container);
        }
    });

    return containers;
}

function normalizeHtmlContent(result: string): string {
    const hasRoot =
        /^\s*<([a-zA-Z]+)(\s|>)/.test(result) &&
        /\s*<\/([a-zA-Z]+)>\s*$/.test(result);

    if (hasRoot) {
        return result;
    }

    if (result.includes("<!DOCTYPE HTML>")) {
        return result.replace(/<!DOCTYPE HTML>/, "<!DOCTYPE HTML><div>");
    }

    return `<div>${result}</div>`;
}

export type {
    ActiveViewType,
    AvailableView,
    Container,
    DashboardState,
    MsAndVerseToOpen,
    TocNavigation,
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
